import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookText, Download, Printer, RefreshCw, Ban, RotateCcw } from "lucide-react";
import { printPadLog } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const logPadIssue = (docType, list, noKey) => {
  const nos = (list || []).map((l) => l[noKey]).filter(Boolean);
  if (!nos.length) return;
  api.post("/pad-issues", { doc_type: docType, start_no: String(nos[0]), end_no: String(nos[nos.length - 1]), count: nos.length }).catch(() => {});
};

export function PadLogButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("All");
  const [branch, setBranch] = useState("All");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const load = () => api.get("/pad-issues").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => {
    if (!open) return;
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [open]);
  const branches = useMemo(() => [...new Set(rows.map((r) => r.office).filter(Boolean))].sort(), [rows]);
  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (type !== "All" && r.doc_type !== type) return false;
        if (branch !== "All" && r.office !== branch) return false;
        const d = String(r.created_at || "").slice(0, 10);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      }),
    [rows, type, branch, from, to]
  );
  return (
    <>
      <Button size="sm" variant="outline" className="bg-white" onClick={() => setOpen(true)} data-testid="pad-log-btn">
        <BookText className="mr-1.5 h-3.5 w-3.5" /> Pad Log
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader><DialogTitle className="font-display">Blank Pad Issue Log</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-end gap-2">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="pad-log-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["All", "LR", "Challan", "MR", "Voucher"].map((t) => (
                  <SelectItem key={t} value={t}>{t === "All" ? "All types" : t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="h-9 w-[140px]" data-testid="pad-log-branch-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" className="h-9 w-[145px]" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="pad-log-from" />
            <Input type="date" className="h-9 w-[145px]" value={to} onChange={(e) => setTo(e.target.value)} data-testid="pad-log-to" />
            {(type !== "All" || branch !== "All" || from || to) && (
              <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setType("All"); setBranch("All"); setFrom(""); setTo(""); }} data-testid="pad-log-filter-clear">Clear</Button>
            )}
            <span className="text-xs text-muted-foreground">{filtered.length} of {rows.length}</span>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={load} title="Recheck usage" data-testid="pad-log-refresh-btn">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" disabled={!filtered.length} onClick={() => printPadLog(filtered)} data-testid="pad-log-print-btn">
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!filtered.length}
                onClick={() => {
                  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
                  const lines = ["When,Type,Start No.,End No.,Count,Issued By,Branch,Used"];
                  filtered.forEach((r) =>
                    lines.push([r.created_at ? `${fmtDate(r.created_at)} ${new Date(r.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "", r.doc_type, r.start_no, r.end_no, r.count, r.issued_by || "", r.office || "", r.voided ? `Cancelled: ${r.void_reason || ""}` : r.serial_count ? `${r.used_count} of ${r.serial_count}` : ""].map(cell).join(","))
                  );
                  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `pad-issue-log-${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                data-testid="pad-log-excel-btn"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
              </Button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["When", "Type", "Serial Range", "Count", "Issued By", "Branch", "Usage"].map((h) => (
                    <TableHead key={h} className="text-[11px] uppercase tracking-[0.12em] text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">{rows.length ? "No entries match the filters." : "No blank pads issued yet."}</TableCell></TableRow>}
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid="pad-log-row">
                    <TableCell className="text-xs">{r.created_at ? `${fmtDate(r.created_at)} ${new Date(r.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "—"}</TableCell>
                    <TableCell className="text-xs font-semibold">{r.doc_type}</TableCell>
                    <TableCell className="text-xs tabular-nums">{r.start_no}{r.end_no && r.end_no !== r.start_no ? ` – ${r.end_no}` : ""}</TableCell>
                    <TableCell className="text-xs tabular-nums">{r.count}</TableCell>
                    <TableCell className="text-xs">{r.issued_by || "—"}</TableCell>
                    <TableCell className="text-xs">{r.office || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {r.voided ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600" title={`Cancelled by ${r.voided_by || "admin"}: ${r.void_reason || ""}`} data-testid="pad-usage-voided">
                          Cancelled
                          {user?.role === "admin" && (
                            <button
                              className="ml-0.5 rounded p-0.5 hover:bg-slate-300"
                              title="Restore this pad range (pad found)"
                              onClick={async () => {
                                if (!window.confirm(`Restore pad range ${r.doc_type} ${r.start_no}–${r.end_no}? Its lost-pad alert will show again until the serials are used.`)) return;
                                try {
                                  await api.put(`/pad-issues/${r.id}/unvoid`);
                                  toast.success("Pad range restored");
                                  load();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || "Failed to restore");
                                }
                              }}
                              data-testid="pad-unvoid-btn"
                            >
                              <RotateCcw className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      ) : !r.serial_count ? (
                        <span className="text-muted-foreground">—</span>
                      ) : r.used_count === r.serial_count ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="pad-usage-all">All used</span>
                      ) : r.used_count > 0 ? (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid="pad-usage-partial">{r.used_count} of {r.serial_count} used</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700" title="No entry made with these serials yet — check for lost pads" data-testid="pad-usage-none">
                          Not used yet
                          {user?.role === "admin" && (
                            <button
                              className="ml-0.5 rounded p-0.5 hover:bg-red-200"
                              title="Cancel this pad range (with reason)"
                              onClick={async () => {
                                const reason = window.prompt(`Cancel pad range ${r.doc_type} ${r.start_no}–${r.end_no}?\nEnter the reason (e.g. pad lost, misprinted):`);
                                if (!reason || !reason.trim()) return;
                                try {
                                  await api.put(`/pad-issues/${r.id}/void`, { reason: reason.trim() });
                                  toast.success("Pad range cancelled");
                                  load();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || "Failed to cancel");
                                }
                              }}
                              data-testid="pad-void-btn"
                            >
                              <Ban className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
